<template>
  <div v-if="optimalFixedRatio">
    <block-item-card
      :title="$t('Risks.Section.DebtProfile.Block.OptimalFixedRatio.Title')"
      :tooltip="$t('Risks.Section.DebtProfile.Block.OptimalFixedRatio.InfoTip')"
    >
      <v-row class="ma-0">
        <v-col
          class="ma-0"
          cols="12"
        >
          <v-row
            class="ma-0"
            justify="center"
            align="center"
          >
            <v-col
              cols="12"
              :md="5"
              :lg="4"
              xl="3"
              class="text-center"
            >
              <v-row>
                <v-col>
                  <asset-button-group
                    align-center
                    min-width="332"
                    :items="ratesViewAssets"
                    :selected.sync="ratesViewSelectedAsset"
                  >
                    <template #title>
                      <v-row class="flex pl-2 pb-1 text-left">
                        <v-col>
                          <span class="text-no-wrap text-size-14 font-weight-medium synthesis-ui-red-03--text">
                            {{ $t('Risks.Section.DebtProfile.Block.OptimalFixedRatio.Buttons.Labels.UserInput1.Prefix') }}
                            <span class="text-no-wrap text-size-14 font-weight-bold synthesis-brand-dark-blue--text">
                              {{ $t('Risks.Section.DebtProfile.Block.OptimalFixedRatio.Buttons.Labels.UserInput1.Suffix') }}
                            </span>
                          </span>
                        </v-col>
                      </v-row>
                    </template>
                  </asset-button-group>
                </v-col>
                <v-col>
                  <asset-button-group
                    align-center
                    min-width="332"
                    :items="riskAversionAssets"
                    :selected.sync="riskAversionSelectedAsset"
                  >
                    <template #title>
                      <v-row class="flex pl-2 pb-1 text-left">
                        <v-col>
                          <span class="text-no-wrap text-size-14 font-weight-medium synthesis-ui-red-03--text">
                            {{ $t('Risks.Section.DebtProfile.Block.OptimalFixedRatio.Buttons.Labels.UserInput2.Prefix') }}
                            <span class="text-no-wrap text-size-14 font-weight-bold synthesis-brand-dark-blue--text">
                              {{ $t('Risks.Section.DebtProfile.Block.OptimalFixedRatio.Buttons.Labels.UserInput2.Suffix') }}
                            </span>
                          </span>
                        </v-col>
                      </v-row>
                    </template>
                  </asset-button-group>
                </v-col>
              </v-row>
            </v-col>

            <v-col
              cols="12"
              :md="7"
              :lg="8"
              xl="9"
            >
              <e-chart-debt-profile-gauge
                axis-x-tick-suffix="%"
                :tooltip-show="false"
                :gauge-progress-show="false"
                :chart-colors="gaugeColorValues"
                :chart-data-options="gaugeValues"
                :chart-animation="true"
                :chart-grid-margin="{left: 0, top: 50, right: 0, bottom: 0, containLabel: true}"
                :chart-height="$vuetify.breakpoint.lgAndUp ? 340 : 260"
                :title-show="true"
                :title-value="$t('Risks.Section.DebtProfile.Block.OptimalFixedRatio.TitleChart')"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </block-item-card>
  </div>
</template>

<script>
import API                    from '@/api/Api'
import Auth                   from '@/mixins/auth'
import BlockItemCard          from '@/theme/default/components/common/BlockItemCard'
import AssetButtonGroup       from '@/theme/default/components/common/AssetButtonGroup.vue'
import EChartDebtProfileGauge from '@/theme/default/components/charts/echarts/EChartDebtProfileGauge.vue'
import baseEChartColors       from '@/theme/default/components/charts/echarts/mixins/common/baseEChartColors'

export default {
  name      : 'HedgingOptimalFixedRatioBlock',
  components: {
    EChartDebtProfileGauge,
    AssetButtonGroup,
    BlockItemCard
  },
  mixins: [Auth, baseEChartColors],
  props : {
    optimalFixedRatio: {
      type   : Object,
      default: null
    }
  },
  data () {
    return {
      ratesViewSelectedAsset   : 'NONE',
      riskAversionSelectedAsset: 'NEUTRAL',
      interestRatesHedgeRatio  : null,
      isApplyingSavedSelection : false
    }
  },
  computed: {
    accountId () {
      return this.user?.selectedAccountId || null
    },

    ratesViewAssets () {
      return (this.optimalFixedRatio?.ratesViews || []).map(item => ({
        Title: item.label,
        Value: item.value
      }))
    },

    riskAversionAssets () {
      return (this.optimalFixedRatio?.riskAversions || []).map(item => ({
        Title: item.label,
        Value: item.value
      }))
    },

    selectedValue () {
      const item = (this.optimalFixedRatio?.items || []).find(item => (
        item.ratesView === this.ratesViewSelectedAsset &&
        item.riskAversion === this.riskAversionSelectedAsset
      )) || null

      return item?.optimalFixedRatio || 0
    },

    gaugeValues () {
      return [{
        value: this.selectedValue
      }]
    },

    gaugeColorValues () {
      const v = this.gaugeColors

      return [
        [0.2, v[0]],
        [0.4, v[1]],
        [0.6, v[2]],
        [0.8, v[3]],
        [1, v[4]]
      ]
    }
  },
  watch: {
    accountId: {
      handler () {
        this.getInterestRatesHedgeRatio()
      },
      immediate: true
    },

    interestRatesHedgeRatio: {
      handler () {
        this.applySavedSelection()
      },
      immediate: true
    },

    ratesViewSelectedAsset () {
      this.saveInterestRatesHedgeRatio()
    },

    riskAversionSelectedAsset () {
      this.saveInterestRatesHedgeRatio()
    }
  },
  beforeCreate () {},
  created () {},
  beforeMount () {},
  mounted () {},
  beforeUpdate () {},
  updated () {},
  beforeDestroy () {},
  destroyed () {},
  methods: {
    getInterestRatesHedgeRatio () {
      if (!this.accountId) {
        this.interestRatesHedgeRatio = null
        return
      }

      API.Resource.Hedging.InterestRatesHedgeRatio(this.accountId)
        .then(response => {
          this.handleInterestRatesHedgeRatioResponse(response)
        })
        .catch(e => {
          this.handleInterestRatesHedgeRatioResponse(e.response)
        })
    },

    handleInterestRatesHedgeRatioResponse (response) {
      const data = API.responseData(response)?.interestRateOptimalHedgeRatio || null
      const errors = API.responseErrors(response) || []

      if (API.isResponseSuccess(response)) {
        this.interestRatesHedgeRatio = data
      } else {
        this.$bus.$emit('app:error', errors)
      }
    },

    saveInterestRatesHedgeRatio () {
      if (this.isApplyingSavedSelection || !this.accountId || !this.optimalFixedRatio) return

      const payload = {
        accountId                            : this.accountId,
        selectedOptimalHedgeRatio            : this.selectedValue,
        selectedOptimalHedgeRatioRatesView   : this.ratesViewSelectedAsset,
        selectedOptimalHedgeRatioRiskAversion: this.riskAversionSelectedAsset
      }

      API.Resource.Hedging.SaveInterestRatesHedgeRatio(payload)
        .then(response => {
          this.handleSaveInterestRatesHedgeRatioResponse(response)
        })
        .catch(e => {
          this.handleSaveInterestRatesHedgeRatioResponse(e.response)
        })
    },

    handleSaveInterestRatesHedgeRatioResponse (response) {
      const errors = API.responseErrors(response) || []

      if (!API.isResponseSuccess(response) && errors.length) {
        this.$bus.$emit('app:error', errors)
      }
    },

    applySavedSelection () {
      if (!this.interestRatesHedgeRatio) return

      this.isApplyingSavedSelection = true
      this.ratesViewSelectedAsset = this.interestRatesHedgeRatio.selectedOptimalHedgeRatioRatesView || 'NONE'
      this.riskAversionSelectedAsset = this.interestRatesHedgeRatio.selectedOptimalHedgeRatioRiskAversion || 'NEUTRAL'

      this.$nextTick(() => {
        this.isApplyingSavedSelection = false
      })
    }
  }
}
</script>

<style scoped>

</style>
